import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  MapPin, Users, BedDouble, Bath, Star, ChevronLeft, Play, Rotate3d,
  CalendarDays, ShieldCheck, Wind, ArrowLeft, Check, Utensils, Waves, Landmark, BadgePercent,
} from "lucide-react";
import { api, money, AMENITY_LABELS } from "@/lib/api";
import { getGuide } from "@/lib/guides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Stars } from "@/components/site/Stars";
import { PhotoLightbox } from "@/components/site/PhotoLightbox";
import { ChannelBadge, VerifiedBadge } from "@/components/site/ReviewMeta";

export default function PropertyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState("2");
  const [upsells, setUpsells] = useState([]);
  const [quote, setQuote] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(null);
  const [lb, setLb] = useState(null);
  const closeLb = useCallback(() => setLb(null), []);
  const [blocked, setBlocked] = useState([]);
  const [refCode, setRefCode] = useState("");
  const [refInfo, setRefInfo] = useState(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setData(r.data)).catch(() => setData("err"));
    api.get(`/properties/${id}/availability`).then((r) => {
      const ranges = (r.data.blocked || []).map((b) => ({
        from: new Date(b.from + "T00:00:00"),
        to: new Date(new Date(b.to + "T00:00:00").getTime() - 86400000), // nights = arrival..departure-1
      }));
      setBlocked(ranges);
    }).catch(() => {});
  }, [id]);

  const iso = (d) => (d ? format(d, "yyyy-MM-dd") : null);

  const rangeHasBlocked = (from, to) => {
    if (!from || !to) return false;
    return blocked.some((b) => from <= b.to && b.from < to);
  };

  const refreshQuote = useCallback(() => {
    if (!range?.from || !range?.to) { setQuote(null); return; }
    api.post("/bookings/quote", {
      property_id: id, check_in: iso(range.from), check_out: iso(range.to),
      guests: Number(guests), upsells,
    }).then((r) => setQuote(r.data)).catch(() => {});
  }, [id, range, guests, upsells]);

  useEffect(() => { refreshQuote(); }, [refreshQuote]);

  const validateRef = async (code) => {
    const c = (code || "").trim();
    if (!c) { setRefInfo(null); return; }
    try {
      const { data } = await api.get(`/referrals/validate/${encodeURIComponent(c)}`);
      setRefInfo(data);
      if (data.valid) localStorage.setItem("elite_ref", data.code);
    } catch { setRefInfo({ valid: false }); }
  };

  useEffect(() => {
    const stored = localStorage.getItem("elite_ref");
    if (stored) { setRefCode(stored); validateRef(stored); }
  }, []);

  const refDiscount = (total) => {
    if (!refInfo?.valid || !total) return 0;
    if (refInfo.discount_type === "percent") return Math.round(total * refInfo.discount_value) / 100;
    if (refInfo.discount_type === "amount") return Math.min(total, refInfo.discount_value);
    return 0;
  };

  const toggleUpsell = (k) => setUpsells((u) => u.includes(k) ? u.filter((x) => x !== k) : [...u, k]);

  const book = async () => {
    if (!range?.from || !range?.to) return toast.error("Please select your dates");
    if (rangeHasBlocked(range.from, range.to)) return toast.error("Your dates include unavailable nights. Please adjust.");
    if (!name || !email) return toast.error("Please enter your name and email");
    setBooking(true);
    try {
      const { data: res } = await api.post("/payments/checkout", {
        property_id: id, check_in: iso(range.from), check_out: iso(range.to),
        guests: Number(guests), upsells, guest_name: name, guest_email: email,
        origin_url: window.location.origin,
        ref_code: refInfo?.valid ? refInfo.code : undefined,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      if (e?.response?.status === 409) toast.error(e.response.data.detail);
      else toast.error("Could not start checkout. Please try again.");
      setBooking(false);
    }
  };

  if (data === "err") return <div className="py-32 text-center text-taupe">Property not found. <Link to="/rentals" className="text-camel underline">Back to rentals</Link></div>;
  if (!data) return <div className="py-32 text-center text-taupe">Loading…</div>;

  const p = data.property;
  const g = p.gallery;

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
      <Link to="/rentals" data-testid="back-to-rentals" className="mb-5 inline-flex items-center gap-1 text-sm text-taupe hover:text-ink">
        <ChevronLeft size={16} /> All rentals
      </Link>

      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl lg:text-5xl">{p.name}</h1>
          <p className="mt-2 flex items-center gap-2 text-taupe">
            <MapPin size={16} strokeWidth={1.5} /> {p.city}, {p.state}
            <span className="mx-1">·</span>
            {p.rating
              ? <><Star size={15} className="fill-gold text-gold" /> {p.rating}{p.review_count > 0 ? ` (${p.review_count} reviews)` : ""}</>
              : <span className="rounded-full bg-cream px-2.5 py-0.5 text-xs font-semibold text-taupe">New listing</span>}
          </p>
          {p.event_type && (
            <span data-testid="event-badge" className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#c9a86a] px-3 py-1 text-xs font-semibold text-ink">
              ★ {p.event_type === "wedding" ? "Perfect for Weddings & Large Gatherings" : "Great for Gatherings"}
            </span>
          )}
        </div>
      </div>

      {/* GALLERY BENTO */}
      {g.length >= 5 ? (
        <div className="relative mb-4 grid grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-3xl" style={{ height: "460px" }}>
          <button onClick={() => setLb(0)} data-testid="gallery-main" className="col-span-2 row-span-2 overflow-hidden">
            <img src={g[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          </button>
          {g.slice(1, 5).map((src, i) => (
            <button key={i} onClick={() => setLb(i + 1)} data-testid={`gallery-${i}`} className="overflow-hidden">
              <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            </button>
          ))}
          <button onClick={() => setMediaOpen("all")} data-testid="gallery-show-all"
            className="absolute bottom-4 right-4 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-ink shadow-md transition-colors hover:bg-white">
            Show all {g.length} photos
          </button>
        </div>
      ) : (
        <div className="mb-4 overflow-hidden rounded-3xl" style={{ height: "460px" }}>
          <button onClick={() => g[0] && setLb(0)} data-testid="gallery-main" className="h-full w-full overflow-hidden bg-cream">
            {g[0]
              ? <img src={g[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
              : <span className="flex h-full items-center justify-center text-taupe">Photos coming soon</span>}
          </button>
        </div>
      )}
      <div className="mb-10 flex gap-3">
        <Button variant="outline" data-testid="video-tour" onClick={() => setMediaOpen("video")} className="rounded-full border-black/10">
          <Play size={15} className="mr-2" /> Video Tour
        </Button>
        <Button variant="outline" data-testid="tour-360" onClick={() => setMediaOpen("360")} className="rounded-full border-black/10">
          <Rotate3d size={15} className="mr-2" /> 360° Tour
        </Button>
      </div>

      <div className="grid gap-12 lg:grid-cols-3">
        {/* LEFT */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-6 border-b border-black/5 pb-8 text-ink">
            <span className="flex items-center gap-2"><BedDouble strokeWidth={1.5} /> {p.bedrooms} Bedrooms</span>
            <span className="flex items-center gap-2"><Bath strokeWidth={1.5} /> {p.bathrooms} Bathrooms</span>
            <span className="flex items-center gap-2"><Users strokeWidth={1.5} /> Sleeps {p.max_guests}</span>
          </div>

          <div className="border-b border-black/5 py-8">
            <h2 className="mb-3 text-2xl">About this home</h2>
            <div className="space-y-4 text-lg leading-relaxed text-ink/80" data-testid="property-description">
              {(p.description || "").split(/\n+/).map((para, i) =>
                para.trim() ? <p key={i}>{para.trim()}</p> : null
              )}
            </div>
          </div>

          <div className="border-b border-black/5 py-8">
            <h2 className="mb-4 text-2xl">Amenities</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {p.tags.map((t) => (
                <span key={t} className="flex items-center gap-2 rounded-xl bg-cream px-4 py-3 text-sm text-ink">
                  <Check size={16} className="text-camel" /> {AMENITY_LABELS[t]}
                </span>
              ))}
              <span className="flex items-center gap-2 rounded-xl bg-cream px-4 py-3 text-sm text-ink"><Wind size={16} className="text-camel" /> WiFi & A/C</span>
            </div>
          </div>

          <div className="border-b border-black/5 py-8">
            <h2 className="mb-4 text-2xl">Sleeping arrangements</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {p.sleeping.map((s, i) => (
                <div key={i} className="rounded-2xl border border-black/5 p-4">
                  <div className="font-medium text-ink">{s.room}</div>
                  <div className="text-sm text-taupe">{s.config}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-b border-black/5 py-8">
            <h2 className="mb-4 text-2xl">Where you'll be</h2>
            <div className="overflow-hidden rounded-2xl border border-black/5">
              <iframe title="property map" className="h-72 w-full"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.lng - 0.05}%2C${p.lat - 0.03}%2C${p.lng + 0.05}%2C${p.lat + 0.03}&layer=mapnik&marker=${p.lat}%2C${p.lng}`} />
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {p.nearby.map((n, i) => <div key={i} className="rounded-xl bg-cream px-4 py-3 text-sm text-ink">{n}</div>)}
            </div>
          </div>

          {(() => {
            const guide = getGuide(p.city);
            const cols = [
              { title: "Where to eat", Icon: Utensils, items: guide.dining },
              { title: "On the water & activities", Icon: Waves, items: guide.activities },
              { title: "Attractions", Icon: Landmark, items: guide.attractions },
            ];
            return (
              <div className="border-b border-black/5 py-8" data-testid="local-guide">
                <h2 className="mb-2 text-2xl">Local guide</h2>
                <p className="mb-6 max-w-2xl text-sm leading-relaxed text-taupe">{guide.intro}</p>
                <div className="grid gap-8 sm:grid-cols-3">
                  {cols.map(({ title, Icon, items }) => (
                    <div key={title}>
                      <div className="mb-3 flex items-center gap-2 text-ink">
                        <Icon size={18} className="text-camel" strokeWidth={1.5} />
                        <span className="font-medium">{title}</span>
                      </div>
                      <ul className="space-y-3">
                        {items.map((it) => (
                          <li key={it.name}>
                            <div className="text-sm font-medium text-ink">{it.name}</div>
                            <div className="text-xs leading-relaxed text-taupe">{it.desc}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="border-b border-black/5 py-8">
            <h2 className="mb-4 text-2xl">House rules</h2>
            <ul className="grid gap-2 sm:grid-cols-2">
              {p.house_rules.map((r, i) => <li key={i} className="text-sm text-ink/80">· {r}</li>)}
            </ul>
          </div>

          <div className="py-8">
            <h2 className="mb-4 flex items-center gap-2 text-2xl">
              <Star className="fill-gold text-gold" /> {p.rating} · {p.review_count} reviews
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {data.reviews.map((rv) => (
                <div key={rv.id} className="rounded-2xl border border-black/5 p-5">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-ink">{rv.author}</span>
                    <ChannelBadge source={rv.source} />
                  </div>
                  <Stars value={rv.rating} size={13} />
                  <p className="mt-2 text-sm text-ink/80">{rv.text}</p>
                  {rv.verified && <div className="mt-2"><VerifiedBadge /></div>}
                </div>
              ))}
              {data.reviews.length === 0 && <p className="text-taupe">No reviews yet.</p>}
            </div>
          </div>
        </div>

        {/* RIGHT — BOOKING */}
        <div>
          <div className="sticky top-24 rounded-3xl card-lux p-6">
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-sm text-taupe">from</span>
              <span className="text-3xl font-medium text-ink">{money(p.nightly_rate)}</span>
              <span className="text-taupe">/ night</span>
            </div>
            {p.estimated_pricing && (
              <p className="mb-4 rounded-xl bg-cream px-3 py-2 text-xs text-taupe" data-testid="estimated-pricing-note">
                Estimated rate — final pricing confirmed by our team after your request.
              </p>
            )}
            {!p.estimated_pricing && (
              <p className="mb-3 text-xs text-taupe">Select your dates for exact live pricing.</p>
            )}
            <div data-testid="direct-savings-badge" className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              <BadgePercent size={15} /> Book direct &amp; save ~15% vs Airbnb — skip the fees
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="booking-dates" className="mb-3 flex w-full items-center gap-2 rounded-2xl border border-black/10 px-4 py-3 text-left">
                  <CalendarDays size={18} className="text-camel" />
                  <span className="text-sm">
                    {range?.from ? (range.to ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}` : format(range.from, "MMM d")) : "Select dates"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1}
                  disabled={[{ before: new Date() }, ...blocked]}
                  modifiers={{ booked: blocked }}
                  modifiersClassNames={{ booked: "line-through opacity-40" }} />
                {blocked.length > 0 && (
                  <div className="border-t border-black/5 px-3 py-2 text-[11px] text-taupe" data-testid="availability-legend">
                    Crossed-out dates are already booked (live from OwnerRez).
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <div className="mb-3">
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger data-testid="booking-guests" className="rounded-2xl border-black/10 py-6">
                  <div className="flex items-center gap-2"><Users size={18} className="text-camel" /><SelectValue /></div>
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: p.max_guests }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} guest{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-3 grid gap-2">
              <Input data-testid="guest-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-2xl border-black/10" />
              <Input data-testid="guest-email" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-2xl border-black/10" />
            </div>

            <div className="mb-3">
              <div className="flex gap-2">
                <Input data-testid="referral-code-input" placeholder="Referral code (optional)" value={refCode}
                  onChange={(e) => setRefCode(e.target.value)} className="rounded-2xl border-black/10 uppercase" />
                <Button data-testid="apply-referral-btn" type="button" variant="outline"
                  onClick={() => validateRef(refCode)} className="rounded-2xl border-black/10">Apply</Button>
              </div>
              {refInfo?.valid && (
                <p data-testid="referral-applied" className="mt-2 text-xs font-semibold text-emerald-700">
                  ✓ Code {refInfo.code} applied{refInfo.discount_type !== "none" ? ` — ${refInfo.discount_type === "percent" ? `${refInfo.discount_value}% off` : `${money(refInfo.discount_value)} off`}` : ""}
                </p>
              )}
              {refInfo && !refInfo.valid && refCode && (
                <p data-testid="referral-invalid" className="mt-2 text-xs font-semibold text-red-600">Invalid or inactive code</p>
              )}
            </div>

            {quote && (
              <div className="mb-4 space-y-2 border-t border-black/5 pt-4 text-sm text-ink/80">
                <Row l={`${money(quote.nightly_rate || p.nightly_rate)} × ${quote.nights} nights`} v={money(quote.accommodation)} />
                {quote.upsells.map((u) => <Row key={u.key} l={u.label} v={money(u.price)} />)}
                <Row l="Taxes & fees" v={money(quote.total - quote.accommodation - quote.upsells.reduce((s, u) => s + (u.price || 0), 0))} />
                {refDiscount(quote.total) > 0 && (
                  <div className="flex justify-between font-semibold text-emerald-700" data-testid="referral-discount-row">
                    <span>Referral ({refInfo.code})</span><span>− {money(refDiscount(quote.total))}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-black/5 pt-2 text-base font-semibold text-ink">
                  <span>Total</span><span data-testid="quote-total">{money(quote.total - refDiscount(quote.total))}</span>
                </div>
                <p className="text-[11px] text-taupe">All taxes and fees included.</p>
                <div data-testid="airbnb-savings" className="mt-1 rounded-xl bg-emerald-50 px-3 py-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-taupe line-through">≈ {money(Math.round(quote.total / 0.85))} on Airbnb</span>
                    <span className="text-sm font-bold text-emerald-700">You save {money(Math.round(quote.total / 0.85) - quote.total)}!</span>
                  </div>
                  <p className="mt-1 text-[11px] font-semibold text-emerald-700">That's ~15% back in your pocket by booking direct.</p>
                </div>
              </div>
            )}

            <Button data-testid="book-now-btn" onClick={book} disabled={booking}
              className="h-12 w-full rounded-full btn-ink text-base">
              {booking ? "Redirecting…" : "Reserve & Pay"}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-taupe">
              <ShieldCheck size={14} /> Instant booking · Secure Stripe checkout
            </p>
          </div>
        </div>
      </div>

      <Dialog open={!!mediaOpen} onOpenChange={() => setMediaOpen(null)}>
        <DialogContent className={mediaOpen === "all" ? "max-w-5xl" : "max-w-3xl"}>
          <DialogHeader><DialogTitle>{mediaOpen === "video" ? "Video Tour" : mediaOpen === "360" ? "360° Virtual Tour" : mediaOpen === "all" ? `All photos (${g.length})` : "Photo"}</DialogTitle><DialogDescription className="sr-only">Property photos and media</DialogDescription></DialogHeader>
          {mediaOpen === "video" || mediaOpen === "360" ? (
            <div className="flex aspect-video items-center justify-center rounded-2xl bg-ink text-white">
              <div className="text-center">
                {mediaOpen === "video" ? <Play size={40} className="mx-auto text-gold" /> : <Rotate3d size={40} className="mx-auto text-gold" />}
                <p className="mt-3 text-sm text-white/60">Immersive {mediaOpen === "video" ? "video" : "360°"} tour — connects to your PMS media library.</p>
              </div>
            </div>
          ) : mediaOpen === "all" ? (
            <div className="grid max-h-[75vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3" data-testid="gallery-all-grid">
              {g.map((src, i) => (
                <img key={i} src={src} alt={`${p.name} photo ${i + 1}`} loading="lazy" data-testid={`gallery-grid-${i}`}
                  onClick={() => { setMediaOpen(null); setLb(i); }}
                  className="aspect-[4/3] w-full cursor-pointer rounded-xl object-cover transition-transform hover:scale-[1.02]" />
              ))}
            </div>
          ) : (
            <img src={mediaOpen} alt="" className="w-full rounded-2xl object-cover" />
          )}
        </DialogContent>
      </Dialog>
      {lb !== null && g.length > 0 && (
        <PhotoLightbox photos={g} index={lb} onChange={setLb} onClose={closeLb} title={p.name} />
      )}
    </div>
  );
}

const Row = ({ l, v }) => (<div className="flex justify-between"><span>{l}</span><span>{v}</span></div>);
